import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, Users, LogOut, Shield, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import AvatarDisplay from "./AvatarDisplay";

/**
 * Sticky bottom navigation for mobile (hidden on md+).
 * active: "rooms" | "friends" | "profile"
 */
export default function BottomNav({ active = "rooms" }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showProfile, setShowProfile] = useState(false);

  const handleLogout = async () => {
    setShowProfile(false);
    await logout();
    navigate("/");
  };

  const TABS = [
    {
      id: "rooms",
      label: "Rooms",
      icon: <Compass className="w-5 h-5" />,
      onClick: () => navigate("/lobby"),
    },
    {
      id: "friends",
      label: "Friends",
      icon: <Users className="w-5 h-5" />,
      onClick: () => navigate("/friends"),
    },
    {
      id: "profile",
      label: user?.alias || "Profile",
      icon: <AvatarDisplay avatarId={user?.avatar_id} size="xs" />,
      onClick: () => setShowProfile(true),
    },
  ];

  return (
    <>
      {/* Bottom nav bar */}
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0E0E17]/95 backdrop-blur-xl border-t border-purple-500/20"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex items-stretch">
          {TABS.map(tab => {
            const isActive = active === tab.id;
            return (
              <button
                key={tab.id}
                data-testid={`bottom-nav-${tab.id}`}
                onClick={tab.onClick}
                className={`flex-1 flex flex-col items-center justify-center py-2.5 gap-1 transition-colors relative ${
                  isActive ? "text-white" : "text-slate-500 hover:text-slate-300"
                }`}
              >
                {/* Active indicator */}
                {isActive && (
                  <motion.div
                    layoutId="bottom-nav-indicator"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-violet-500 to-cyan-400 rounded-full"
                  />
                )}
                <span className={isActive ? "text-white" : ""}>{tab.icon}</span>
                <span className="text-[10px] font-medium truncate max-w-[64px]">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Profile sheet */}
      <AnimatePresence>
        {showProfile && (
          <>
            {/* Backdrop */}
            <div
              className="md:hidden fixed inset-0 bg-black/60 z-[60]"
              onClick={() => setShowProfile(false)}
            />
            {/* Slide-up sheet */}
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 320 }}
              className="md:hidden fixed bottom-0 left-0 right-0 z-[70] bg-[#0E0E17] border-t border-purple-500/25 rounded-t-2xl shadow-2xl"
              style={{ paddingBottom: "env(safe-area-inset-bottom, 16px)" }}
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-slate-700" />
              </div>

              <div className="px-5 pb-5">
                {/* User info */}
                <div className="flex items-center gap-3 py-4 border-b border-purple-500/15 mb-4">
                  <AvatarDisplay avatarId={user?.avatar_id} size="lg" status="online" showStatus />
                  <div className="flex-1 min-w-0">
                    <p className="text-xl font-black text-white font-[Outfit] truncate">{user?.alias}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span className="text-xs text-emerald-400">Online</span>
                    </div>
                  </div>
                  <button onClick={() => setShowProfile(false)} className="text-slate-500 hover:text-slate-300 p-1">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  {user?.role === "admin" && (
                    <button
                      data-testid="profile-sheet-admin-btn"
                      onClick={() => { navigate("/admin"); setShowProfile(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 font-medium text-sm hover:bg-cyan-500/15 transition-colors"
                    >
                      <Shield className="w-4 h-4" />
                      Admin & Moderation Panel
                    </button>
                  )}
                  <button
                    data-testid="profile-sheet-logout-btn"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#141322] border border-slate-700 text-slate-300 text-sm hover:text-red-400 hover:border-red-500/30 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
